import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs, limit, startAfter, getDoc, doc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { ArrowLeft, ChevronLeft, Zap, GraduationCap, Globe, Heart, User, Sparkles, Settings, RefreshCw, Menu, LogOut } from 'lucide-react';
import { signOut } from 'firebase/auth';
import { getAnonymousName, getAvatarForShadow } from '../utils/shadowIdentity';
import { useIdentity } from '../context/IdentityContext';
import { useNavigationCache } from '../context/NavigationCacheContext';

const formatCount = (n) => {
    if (!n) return '0';
    if (n < 1000) return n.toString();
    if (n < 1000000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
    return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
};

const globalProfileCache = {
  shadows: [],
  hasMore: true,
  activeTab: 'all',
  scrollY: 0,
  isInitialized: false,
  userCollege: '',
  userIsPremium: false,
};

export const ShadowProfileScreen = () => {
    const navigate = useNavigate();
    const currentUser = auth.currentUser;
    const { identityMode, toggleIdentity } = useIdentity();

    const [activeTab, setActiveTab] = useState(globalProfileCache.activeTab);
    const [shadows, setShadows] = useState(globalProfileCache.shadows);
    const [loading, setLoading] = useState(!globalProfileCache.isInitialized);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(globalProfileCache.hasMore);

    const [userCollege, setUserCollege] = useState(() => {
        if (globalProfileCache.userCollege) return globalProfileCache.userCollege;
        if (currentUser) return localStorage.getItem(`spark_user_college_${currentUser.uid}`) || '';
        return '';
    });
    const [userIsPremium, setUserIsPremium] = useState(globalProfileCache.userIsPremium);
    const [showLogoutMenu, setShowLogoutMenu] = useState(false);

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate('/login');
        } catch (error) {}
    };

    const currentTabRef = useRef(activeTab);
    const lastDocRef = useRef(null);
    const BATCH_SIZE = 15;

    useEffect(() => {
        if (!currentUser) return;
        
        const cached = globalProfileCache.userCollege || localStorage.getItem(`spark_user_college_${currentUser.uid}`);
        if (cached) {
            setUserCollege(cached);
            globalProfileCache.userCollege = cached;
        } else {
            getDoc(doc(db, 'users', currentUser.uid)).then(d => {
                if (d.exists()) {
                    const collegeName = d.data().college || '';
                    const isPrem = d.data().isPremium || false;
                    setUserCollege(collegeName);
                    setUserIsPremium(isPrem);
                    globalProfileCache.userCollege = collegeName;
                    globalProfileCache.userIsPremium = isPrem;
                    if (collegeName) {
                        localStorage.setItem(`spark_user_college_${currentUser.uid}`, collegeName);
                    }
                }
            });
        }
    }, [currentUser]);

    const fetchShadows = useCallback(async (isLoadMore = false, tab = activeTab) => {
        if (!currentUser) return;
        try {
            if (!isLoadMore) setLoading(true);
            else setLoadingMore(true);

            const constraints = [
                where('userId', '==', currentUser.uid),
                orderBy('createdAt', 'desc'),
                limit(BATCH_SIZE)
            ];

            if (tab !== 'all') {
                constraints.splice(1, 0, where('visibility', '==', tab));
            }

            if (isLoadMore && lastDocRef.current) {
                constraints.push(startAfter(lastDocRef.current));
            }

            const q = query(collection(db, 'confessions'), ...constraints);
            const snapshot = await getDocs(q);

            if (currentTabRef.current !== tab) return;

            const fetched = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            if (isLoadMore) {
                setShadows(prev => [...prev, ...fetched]);
            } else {
                setShadows(fetched);
            }

            lastDocRef.current = snapshot.docs[snapshot.docs.length - 1] || null;
            setHasMore(snapshot.docs.length === BATCH_SIZE);

        } catch (err) {
            console.error('Failed to fetch profile shadows', err);
        } finally {
            if (currentTabRef.current === tab) {
                setLoading(false);
                setLoadingMore(false);
            }
        }
    }, [currentUser, activeTab]);

    // Sync state into global cache
    useEffect(() => {
        if (shadows.length > 0) {
            globalProfileCache.shadows = shadows;
            globalProfileCache.hasMore = hasMore;
            globalProfileCache.activeTab = activeTab;
            globalProfileCache.isInitialized = true;
        }
    }, [shadows, hasMore, activeTab]);

    // Restore scroll position on mount
    useEffect(() => {
        if (globalProfileCache.scrollY > 0) {
            const savedY = globalProfileCache.scrollY;
            window.scrollTo(0, savedY);
            const timer = setTimeout(() => {
                window.scrollTo(0, savedY);
            }, 50);
            return () => clearTimeout(timer);
        }
    }, []);

    // Track scroll position continuously
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 0) {
                globalProfileCache.scrollY = window.scrollY;
            }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Initial fetch only when not cached or tab changes
    useEffect(() => {
        const tabChanged = currentTabRef.current !== activeTab;
        if (tabChanged || !globalProfileCache.isInitialized) {
            currentTabRef.current = activeTab;
            setHasMore(true);
            lastDocRef.current = null;
            fetchShadows(false, activeTab);
        }
    }, [activeTab, currentUser, fetchShadows]);

    // Infinite scroll
    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 500) {
                if (!loadingMore && hasMore && !loading) {
                    fetchShadows(true, activeTab);
                }
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [fetchShadows, loadingMore, hasMore, loading, activeTab]);

    const handleTabChange = (tab) => {
        if (activeTab === tab) return;
        setActiveTab(tab);
    };

    const shadowName = currentUser ? getAnonymousName(currentUser.uid) : 'Anonymous';
    const shadowAvatar = getAvatarForShadow(shadowName);

    return (
        <main className="w-full sm:w-[700px] min-h-screen border-r border-gray-800 flex flex-col pb-20 sm:pb-0 shrink-0 bg-black">
            {/* Header */}
            <div className="sticky top-0 bg-black/80 backdrop-blur-md z-40 border-b border-gray-800 flex items-center justify-between h-[60px] px-4">
                <div className="flex items-center">
                    <button 
                        onClick={() => navigate('/')}
                        className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors mr-2"
                    >
                        <ChevronLeft size={24} className="text-white" />
                    </button>
                    <h1 className="text-xl font-extrabold text-white">Shadow Profile</h1>
                </div>
                <div className="flex items-center gap-1 sm:hidden">
                    <button 
                        onClick={toggleIdentity}
                        title="Switch Identity"
                        className={`p-2 rounded-full transition-colors ${identityMode === 'shadow' ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30' : 'bg-[#0BD9E5]/20 text-[#0BD9E5] hover:bg-[#0BD9E5]/30'}`}
                    >
                        <RefreshCw size={18} />
                    </button>
                    <button 
                        onClick={() => navigate('/settings')}
                        className="p-2 hover:bg-white/10 rounded-full transition-colors text-gray-400"
                    >
                        <Settings size={20} />
                    </button>
                    
                    {/* Hamburger menu for logout */}
                    <div className="relative">
                        <button 
                            onClick={() => setShowLogoutMenu(!showLogoutMenu)}
                            className="p-2 hover:bg-white/10 rounded-full transition-colors text-gray-400"
                        >
                            <Menu size={20} />
                        </button>
                        {showLogoutMenu && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={() => setShowLogoutMenu(false)} />
                                <div className="absolute right-0 top-full mt-2 w-48 bg-black border border-gray-800 rounded-2xl shadow-xl z-50 overflow-hidden py-1">
                                    <button 
                                        onClick={handleLogout}
                                        className="w-full px-4 py-3 text-left hover:bg-white/10 transition-colors flex items-center gap-3 font-bold text-red-500 text-[15px]"
                                    >
                                        <LogOut size={18} /> Log out
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Profile Info */}
            <div className="flex flex-col items-center py-8 border-b border-gray-800">
                <div className="w-20 h-20 rounded-full bg-gray-800 overflow-hidden flex items-center justify-center border-2 border-gray-700 mb-4 shadow-xl">
                    {shadowAvatar ? (
                        <img src={shadowAvatar} className="w-full h-full object-cover" alt={shadowName} />
                    ) : (
                        <User size={32} className="text-gray-500" />
                    )}
                </div>                <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-2xl font-bold text-white">{shadowName}</h2>
                    {userIsPremium && <Sparkles size={20} className="text-[#0BD9E5]" />}
                </div>
                <p className="text-gray-500 font-medium mb-3">{userCollege || 'Unknown College'}</p>
                <div className="bg-purple-500/10 px-4 py-1.5 rounded-full border border-purple-500/20 mb-6">
                    <span className="text-purple-400 font-bold text-[13px] tracking-wide uppercase">Shadow Identity</span>
                </div>

                <div className="flex flex-col items-center">
                    <span className="text-3xl font-black text-white">{formatCount(shadows.length)}</span>
                    <span className="text-gray-500 text-[13px] uppercase tracking-wide font-bold">Shadows</span>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex w-full h-[50px] border-b border-gray-800 bg-[#111]">
                {['all', 'campus', 'global'].map(tab => {
                    const isActive = activeTab === tab;
                    let Icon = Zap;
                    if (tab === 'campus') Icon = GraduationCap;
                    if (tab === 'global') Icon = Globe;
                    
                    return (
                        <button 
                            key={tab}
                            onClick={() => handleTabChange(tab)}
                            className={`flex-1 flex flex-col justify-center items-center relative transition-colors ${isActive ? 'bg-white/5' : 'hover:bg-white/5'}`}
                        >
                            <div className="flex items-center gap-2">
                                <Icon size={16} className={isActive ? (tab === 'all' ? 'text-[#0BD9E5]' : 'text-white') : 'text-gray-500'} />
                                <span className={`text-[14px] font-bold ${isActive ? 'text-white' : 'text-gray-500'}`}>
                                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                </span>
                            </div>
                            {isActive && (
                                <div className="absolute bottom-0 w-full h-[3px] bg-white rounded-t-full" />
                            )}
                        </button>
                    )
                })}
            </div>

            {/* Grid */}
            <div className="p-1">
                {loading ? (
                    <div className="flex justify-center p-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
                    </div>
                ) : shadows.length === 0 ? (
                    <div className="text-center p-12 text-gray-500 font-medium">
                        You haven't posted any shadows yet.
                    </div>
                ) : (
                    <div className="grid grid-cols-3 gap-[2px]">
                        {shadows.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => navigate(`/shadow/${item.id}`)}
                                className="aspect-square relative bg-gray-900 overflow-hidden hover:opacity-90 transition-opacity flex flex-col"
                            >
                                {item.mediaUrl || item.imageUrl ? (
                                    <img src={item.mediaUrl || item.imageUrl} className="w-full h-full object-cover" alt="Shadow" />
                                ) : (
                                    <div className="w-full h-full p-2 flex items-center justify-center text-center">
                                        <p className="text-[10px] sm:text-[11px] text-gray-300 line-clamp-6 leading-tight break-words">
                                            {item.content}
                                        </p>
                                    </div>
                                )}
                                <div className="absolute bottom-1 right-1 bg-black/60 backdrop-blur-md px-1.5 py-0.5 rounded flex items-center gap-1">
                                    <Heart size={10} className="text-pink-500" fill="currentColor" />
                                    <span className="text-[10px] text-white font-bold">{formatCount(item.likes || 0)}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
                {loadingMore && (
                    <div className="flex justify-center p-6">
                        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-purple-500"></div>
                    </div>
                )}
            </div>
        </main>
    );
};
